import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import ProductDrawerAction from "@/components/drawers/ProductDrawer/ProductDrawerAction";
import HelpButton from "@/components/HelpButton/HelpButton";
import IncompleteIcon from "@/components/icons/chapters/IncompleteIcon";
import ProxyIcon from "@/components/icons/ProxyIcon";
import ImpactPill from "@/components/Impact/ImpactPill";
import impactPillStyles from "@/components/Impact/ImpactPill.module.scss";
import MoreButton from "@/components/MoreButton/MoreButton";
import { PageHeaderNew } from "@/components/PageHeader/PageHeaderNew";
import Skeleton from "@/components/Skeleton/Skeleton";
import StatusTag from "@/components/StatusTag/StatusTag";
import PrimarySourceTag from "@/components/StatusTags/PrimarySourceTag/PrimarySourceTag";
import ProxyTag from "@/components/StatusTags/ProxyTag/ProxyTag";
import TooltipLink from "@/components/TooltipLink/TooltipLink";
import { useCompanyAccess } from "@/hooks/useCompanySettings";
import useImpactMethod from "@/hooks/useImpactMethod";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import { extractImpactMetric } from "@/pages/dashboard/hooks/useImpactMetric";
import { useProductImpactTotalsPerLifecyclePerProduct } from "@/pages/dashboard/hooks/useProductImpact";
import { formatMass } from "@/pages/dashboard/utils/formatters";
import { productLabel } from "@/utilities/products";
import { DeleteOutlined, EditOutlined, PieChartOutlined, SubnodeOutlined } from "@ant-design/icons";
import { Button, Flex, Space, Tooltip } from "antd";
import { Outlet, useNavigate } from "react-router";
import { Link, useParams } from "react-router-dom";
import DeleteProductAction from "../actions/DeleteProductAction/DeleteProductAction";
import DeleteProxyAction from "../actions/DeleteProxyAction/DeleteProxyAction";
import EditProductModal from "../actions/EditProductAction/EditProductAction";
import ManageProxyAction from "../actions/ManageProxyAction/ManageProxyAction";
import { PRODUCTS_BREADCRUMBS } from "../breadcrumbs";
import { useGetProduct, useGetProductRelations, usePatchProduct } from "../useProducts";
import styles from "./ProductDetail.module.scss";
import ProductDetailTabs from "./ProductDetailTabs";
import useProductDetailTabs from "./useProductDetailTabs";

export default function ProductsLayout() {
  useTrackOnLoad("Product Viewed");

  let { productId: id } = useParams();
  const productId = Number(id);

  const access = useCompanyAccess();
  const { environmentalCostMetric, isLoading: metricIsLoading } = useImpactMethod();

  const { data, isLoading } = useGetProduct({
    productId,
  });

  const { data: relationsData } = useGetProductRelations({ productId });

  const { data: productImpact, isLoading: productImpactLoading } =
    useProductImpactTotalsPerLifecyclePerProduct({
      productId,
      by: "single",
      metrics: [environmentalCostMetric, "carbon_footprint"],
    });

  const patchProduct = usePatchProduct();

  const navigate = useNavigate();

  const { activeTab } = useProductDetailTabs({ productId });

  const product = data?.product;
  const primarySource = relationsData?.find((x) => x.relation.includes("primary_source"));
  const isPrimarySource = product?.relation?.includes("primary_source");
  const isProxy = product?.relation?.includes("proxy");
  const hasNoAccess = access && !access.includes("products");
  const hasDisabledContent = product?.proxy_id !== null && activeTab !== "relations";

  const usedMassIcon = (
    <Tooltip title="Mass used in product calculations">
      <PieChartOutlined />
    </Tooltip>
  );

  return (
    <>
      <div className={styles.ProductDetail}>
        <PageHeaderNew
          loading={isLoading}
          title={product ? productLabel(product?.external_id, product?.name) : ""}
          extraTop={
            primarySource &&
            !isLoading && (
              <div className={styles.proxy}>
                <div className={styles.container}>
                  <Flex
                    gap={12}
                    align="center"
                  >
                    <ProxyIcon />
                    <Link to={`/products/${primarySource.id}`}>
                      <Flex
                        gap={8}
                        align="center"
                      >
                        <span>This product is a proxy of</span>
                        <span className={styles.label}>
                          {productLabel(primarySource.external_id, primarySource.name)}
                        </span>
                      </Flex>
                    </Link>
                  </Flex>
                  <Flex
                    gap={8}
                    align="center"
                  >
                    <Space.Compact>
                      <ManageProxyAction
                        state={"edit"}
                        proxyIds={[productId]}
                        proxyIdsDisabled={true}
                        sourceId={product?.proxy_id}
                        multiplier={product?.proxy_weight}
                      >
                        <Button>Manage proxy</Button>
                      </ManageProxyAction>
                      <DeleteProxyAction proxyIds={[productId]}>
                        <Button>
                          <DeleteOutlined style={{ color: "red" }} />
                        </Button>
                      </DeleteProxyAction>
                    </Space.Compact>
                  </Flex>
                </div>
              </div>
            )
          }
          breadcrumbsRight={
            <HelpButton
              url="/"
              type="text"
            />
          }
          breadcrumbs={PRODUCTS_BREADCRUMBS}
          titleRight={
            <Space.Compact>
              <EditProductModal productId={productId}>
                <Button icon={<EditOutlined />}>Edit details</Button>
              </EditProductModal>
              {!isLoading && !isProxy && !isPrimarySource && (
                <ManageProxyAction
                  state={"add"}
                  proxyIds={[productId]}
                  proxyIdsDisabled
                >
                  <Button>
                    <SubnodeOutlined /> Add proxy
                  </Button>
                </ManageProxyAction>
              )}
              <MoreButton
                actions={
                  <>
                    <ActionsPopOverItem
                      icon={<EditOutlined />}
                      disabled={isLoading || patchProduct.isLoading}
                      loading={isLoading || patchProduct.isLoading}
                      onClick={() =>
                        patchProduct.mutateAsync({
                          productId: productId,
                          patch: {
                            is_out_of_scope: !data?.product.is_out_of_scope,
                          },
                        })
                      }
                    >
                      {data?.product.is_out_of_scope ? "Include in scope" : "Exclude from scope"}
                    </ActionsPopOverItem>
                    <DeleteProductAction
                      products={[{ deletable: product?.deletable || false, id: productId }]}
                      onSucces={() => navigate("/products")}
                    >
                      <>
                        <ActionsPopOverItem
                          disabled={!product?.deletable}
                          danger
                          icon={<DeleteOutlined />}
                        >
                          Delete product
                        </ActionsPopOverItem>
                      </>
                    </DeleteProductAction>
                  </>
                }
              />
            </Space.Compact>
          }
        >
          <Flex justify="space-between">
            <EditProductModal
              productId={productId}
              customRenderer={(props) => (
                <div
                  className={styles.attributes}
                  {...props}
                >
                  <span className={styles.attribute}>
                    <span className={styles.title}>Category</span>
                    <span className={styles.value}>{product?.category || "-"}</span>
                  </span>
                  <span className={styles.attribute}>
                    <span className={styles.title}>BOM mass</span>
                    <span className={styles.value}>
                      {product?.bom_mass ? formatMass(product?.bom_mass) : "-"}
                      {product?.bom_mass && usedMassIcon}
                    </span>
                  </span>
                  <span className={styles.attribute}>
                    <span
                      className={
                        product?.mass_status === "incomplete" ? styles.incomplete : styles.complete
                      }
                    >
                      <span className={styles.title}>Added mass</span>
                      <span className={styles.value}>
                        {product?.mass ? formatMass(product?.mass) : "-"}
                        {!product?.bom_mass && product?.mass && usedMassIcon}
                      </span>
                      {product?.mass_status === "incomplete" && <IncompleteIcon />}
                    </span>
                  </span>
                  <span className={styles.attribute}>
                    <span
                      className={
                        product?.packaging_status === "incomplete"
                          ? styles.incomplete
                          : styles.complete
                      }
                    >
                      <span className={styles.title}>Packaging</span>
                      <span className={styles.value}>
                        {product?.packaging_ids.length && product?.packaging_ids.length > 0
                          ? `${product?.packaging_ids.length} packaging`
                          : !product?.has_packaging
                            ? "No"
                            : "-"}
                      </span>
                    </span>
                  </span>
                </div>
              )}
            />
            <Flex
              gap={8}
              align="center"
            >
              <ImpactPill
                blur={hasNoAccess}
                type="borderless"
                loading={productImpactLoading || metricIsLoading}
                impact={productImpact?.total_impact}
                showDate={true}
                carbonRenderer={(children) => (
                  <ProductDrawerAction
                    className={impactPillStyles.child}
                    productId={productId}
                    metric={extractImpactMetric("carbon_footprint")}
                    by={"single"}
                    disabled={hasNoAccess}
                  >
                    {children}
                  </ProductDrawerAction>
                )}
                environmentalRenderer={(children) => (
                  <ProductDrawerAction
                    className={impactPillStyles.child}
                    productId={productId}
                    metric={extractImpactMetric(environmentalCostMetric)}
                    by={"single"}
                    disabled={hasNoAccess}
                  >
                    {children}
                  </ProductDrawerAction>
                )}
              />
              {isLoading ? (
                <Skeleton style={{ width: "90px", height: "24px" }} />
              ) : (
                product && (
                  <StatusTag
                    background="white"
                    status={product.status}
                    showIcon={false}
                  />
                )
              )}
              <>
                {isPrimarySource ? (
                  <PrimarySourceTag
                    to={`/products/${productId}/relations`}
                    num={relationsData?.length}
                  />
                ) : isProxy ? (
                  <ProxyTag />
                ) : (
                  ""
                )}
              </>
            </Flex>
          </Flex>
        </PageHeaderNew>
        <div className={styles.tabs}>
          <ProductDetailTabs productId={productId} />
        </div>
        <Tooltip
          placement="top"
          className={hasDisabledContent ? styles.disabled : ""}
          title={
            hasDisabledContent ? (
              <>
                Editing bill of materials, usage and end of life is disabled for proxy products.
                Instead,{" "}
                <TooltipLink to={`/products/${product?.proxy_id}/${activeTab}`}>
                  edit the source product
                </TooltipLink>
                .
              </>
            ) : undefined
          }
        >
          {hasDisabledContent && <div className={styles.overlay}></div>}
          <>
            <Outlet />
          </>
        </Tooltip>
      </div>
    </>
  );
}
